import { TabConfig } from '@odin-labs/components';
import { to } from 'acl';
import { SitePermission } from 'apollo/generated/client-operations';
import { AuthUser } from 'auth/types';
import type { SitePermissions } from 'sitePermissions/useSitePermissions';
import { ensureNonEmptyItems } from 'utils';
import { JobsiteConfigTabProps } from './types';
import {
  DevicesTab,
  DocumentsTab,
  FeaturesTab,
  FormsTab,
  GeofencingTab,
  LegacySiteLayoutTab,
  SiteLayoutTab,
  JobsiteInfoTab,
  NotificationsTab,
  OnboardingTab,
  SiteAccessRulesTab,
  WorkerInfoTab,
  HidFargoConnectTab,
  CCureTab,
  AvigilonTab,
  ConnectorsTab,
  ProcoreTab,
  CustomFieldsTab,
  AccessGroupsTab,
  DataSharingTab,
} from './tabs';

/**
 * The jobsite settings tabs a user sees. The tabs that predate zones need
 * the legacy `editJobsiteConfiguration` permission at this site. The zones tabs
 * (Site Layout, Devices, Access groups) wait for the site's flag and are
 * gated by the API's site permission catalog, so an Odin Ambassador Admin
 * sees those the catalog grants it at an assigned site (Devices, until M2
 * and M4 bring layout and groups), alongside their existing legacy tabs; while the catalog
 * loads, or the site changes, they are hidden.
 */
export const getTabsConfig = ({
  siteLayoutUiEnabled,
  jobsiteId,
  user,
  sitePermissions,
}: {
  siteLayoutUiEnabled: boolean;
  jobsiteId: string;
  user: AuthUser;
  sitePermissions: SitePermissions;
}): TabConfig<JobsiteConfigTabProps>[] => {
  const legacy = user.isAllowed(to.editJobsiteConfiguration, jobsiteId);
  const zones = (permission: SitePermission): boolean =>
    siteLayoutUiEnabled &&
    !sitePermissions.loading &&
    sitePermissions.can(permission);
  return ensureNonEmptyItems<TabConfig<JobsiteConfigTabProps>>([
    legacy && {
      name: 'jobsiteInfo',
      text: 'Jobsite Info',
      relativePath: '',
      component: JobsiteInfoTab,
    },
    legacy && {
      name: 'forms',
      text: 'Logs',
      relativePath: '/forms',
      component: FormsTab,
    },
    legacy && {
      name: 'features',
      text: 'Features',
      relativePath: '/features',
      component: FeaturesTab,
    },
    legacy && {
      name: 'onboarding',
      text: 'Onboarding',
      relativePath: '/onboarding',
      component: OnboardingTab,
    },
    legacy && {
      name: 'workerInfo',
      text: 'Worker Info',
      relativePath: '/worker-info',
      component: WorkerInfoTab,
    },
    legacy && {
      name: 'documents',
      text: 'Documents',
      relativePath: '/documents',
      component: DocumentsTab,
    },
    // The layout designer's tabs, shown while the jobsite-scoped
    // `site-layout-ui-enabled` flag is on.
    zones(SitePermission.ViewSiteLayout) && {
      name: 'siteLayout',
      text: 'Site Layout',
      relativePath: '/site-layout',
      component: SiteLayoutTab,
    },
    zones(SitePermission.ViewSiteDevices) && {
      name: 'devices',
      text: 'Devices',
      relativePath: '/devices',
      component: DevicesTab,
    },
    legacy && {
      name: 'siteAccessRules',
      text: 'Site Access Rules',
      relativePath: '/site-access-rules',
      component: SiteAccessRulesTab,
    },
    legacy && {
      name: 'notifications',
      text: 'Notifications',
      relativePath: '/notifications',
      component: NotificationsTab,
    },
    // Off, the Site Layout tab as it was (geofencing, gateways, and the
    // read-only zones list) takes Geofencing's place, after Notifications,
    // where it was before the designer.
    legacy &&
      (siteLayoutUiEnabled
        ? {
            name: 'geofencing',
            text: 'Geofencing',
            relativePath: '/geofencing',
            component: GeofencingTab,
          }
        : {
            name: 'siteLayout',
            text: 'Site Layout',
            relativePath: '/site-layout',
            component: LegacySiteLayoutTab,
          }),
    legacy && {
      name: 'hidFargoConnect',
      text: 'HID Fargo Connect',
      relativePath: '/hid-fargo-connect',
      component: HidFargoConnectTab,
    },
    legacy && {
      name: 'cCURE',
      text: 'C-CURE',
      relativePath: '/c-cure',
      component: CCureTab,
    },
    user.isAllowed(to.seeSiteAvigilon) && {
      name: 'avigilon',
      text: 'Avigilon',
      relativePath: '/avigilon',
      component: AvigilonTab,
    },
    user.isAllowed(to.seeSiteConnectors) && {
      name: 'connectors',
      text: 'Connectors',
      relativePath: '/connectors',
      component: ConnectorsTab,
    },
    legacy && {
      name: 'procore',
      text: 'Procore',
      relativePath: '/procore',
      component: ProcoreTab,
    },
    legacy && {
      name: 'customFields',
      text: 'Custom Fields',
      relativePath: '/custom-fields',
      component: CustomFieldsTab,
    },
    // Zones, access groups included, wait for the site's flag.
    zones(SitePermission.ViewSiteGroups) && {
      name: 'accessGroups',
      text: 'Access groups',
      relativePath: '/access-groups',
      component: AccessGroupsTab,
    },
    // Sharing one site's data with another client is a platform-level action,
    // so it's gated on its own `seeSiteDataSharing` permission rather than
    // the legacy tabs' `editJobsiteConfiguration` one.
    user.isAllowed(to.seeSiteDataSharing) && {
      name: 'dataSharing',
      text: 'Data Sharing',
      relativePath: '/data-sharing',
      component: DataSharingTab,
    },
  ]);
};
